import { Store } from "./Store";
import ListPageUtils from "../utils/ListPageUtils";
import TrackingUtil from "../utils/TrackingUtil";
import simplesocket from "../uidispatcherapi/SimpleSocket";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import * as ListOverviewUtils from "../utils/ListOverviewUtils";
import { ShoppingListItem } from "../types/ShoppingListItem";
import { ShoppingList, MetaData } from "../types/ShoppingList";

import { isTier12Customer } from "./SessionStore";

class SimpleSocketListStore extends Store {
    _currentStoreId: string | undefined | null;
    _currentCustomerId: string | undefined | null;
    _subSocket: any;
    _timer: any;

    constructor() {
        super();
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this._currentStoreId = null;
        this._currentCustomerId = null;
        this._subSocket = null;
        this._timer = null;
        this.onSessionStoreTrigger();
    }

    onSessionStoreTrigger() {
        const storeId = SessionStore.getCustomerStore();
        const customerId = SessionStore.getCustomerId();
        const storeIdChanged = storeId !== this._currentStoreId;
        const customerIdChanged = customerId !== this._currentCustomerId;

        if (!this.isLoggedInTier34CustomerOrEmployee()) {
            return;
        }

        if (storeIdChanged || customerIdChanged) {
            this.initWS();
        }

        this._currentStoreId = storeId;
        this._currentCustomerId = customerId;
    }

    initWS() {
        if (this._subSocket) {
            this._subSocket.close();
        }
        const userId = SessionStore.getCustomerId();
        if (!userId) {
            return;
        }
        const storeId = SessionStore.getCustomerStore();
        if (!storeId) {
            return;
        }
        const basePath = SessionStore.isEmployee() ? "/ex/v1/personallists/customerId/" : "/ex/v1/list/";

        const request = {
            channel: basePath + encodeURIComponent(userId), // };
            onOpenFailed: (response: any) => {
                // No error cause we can not cover it.
                TrackingUtil.trackUiErrorCa("SimpleSocketListStore", "Could not open websocket ", response);
                console.warn("Sorry, something went wrong with PersonalList,onOpenFailed: " + response);
                this.webSocketFailed();
            },
            onClose: () => {
                console.warn("Sorry, something went wrong with PersonalList,onClose.");
                this.webSocketFailed();
            },
            onMessage: (result: any) => {
                if (this._timer) {
                    window.clearInterval(this._timer);
                    this._timer = undefined;
                }
                if (typeof result.eventType !== "undefined") {
                    if (result.eventType === "CHANGE_LIST_BASE_DATA") {
                        const newMetaData = result.listBaseData;
                        const listId = newMetaData.personalListId;
                        this.trigger({
                            listId: listId,
                            listUpdateFn: this.changeListMetaDataViaSimpleSocket(newMetaData)
                        });
                    } else if (result.eventType === "CHANGE_LIST") {
                        const newList = result.list;
                        const listId = newList.listId;
                        this.trigger({
                            listId: listId,
                            listUpdateFn: this.changeListViaSimpleSocket(newList)
                        });
                    } else if (result.eventType === "DELETE_LIST") {
                        const listId = result.listId;
                        this.trigger({
                            listId: listId,
                            listUpdateFn: this.removeListViaSimpleSocket()
                        });
                    } else if (result.eventType === "CHANGE_ITEMS") {
                        const listId = result.listId;
                        this.trigger({
                            listId: listId,
                            listUpdateFn: this.changeItemsViaSimpleSocket(result.itemsToAdd, result.itemsToRemove)
                        });
                    }
                } else if (typeof result.lists !== "undefined") {
                    this.trigger({
                        newLists: result.lists
                    });
                }
            }
        };
        this._subSocket = simplesocket.initConnection(request);
    }

    // For testing
    getSubSocket() {
        return this._subSocket;
    }

    webSocketFailed() {
        if (!this._timer && this.isLoggedInTier34CustomerOrEmployee()) {
            this._timer = window.setInterval(() => {
                this.onSessionStoreTrigger();
            }, 2000);
        }
    }

    isLoggedInTier34CustomerOrEmployee() {
        return SessionStore.loggedIn() && !isTier12Customer();
    }

    updateListMetaData(oldPersonalList: ShoppingList, newMetaData: MetaData) {
        return Object.assign({}, oldPersonalList, {
            name: newMetaData.name,
            storeId: newMetaData.storeId,
            color: newMetaData.color,
            globallyVisible: newMetaData.globallyVisible,
            readOnlyForEmployees: newMetaData.readOnlyForEmployees,
            readOnly: newMetaData.readOnlyForCustomer,
            restrictedForCustomer: newMetaData.restrictedForCustomer,
            validFromDate: newMetaData.validFromDate,
            validToDate: newMetaData.validToDate,
            lifecycleInformation: newMetaData.lifecycle,
            userId: newMetaData.userId,
            visibleForCustomer: newMetaData.visibleForCustomer,
            uniqAmount: oldPersonalList.uniqAmount
        });
    }

    changeListViaSimpleSocket(newList: ShoppingList) {
        return (storedList: ShoppingList | null | undefined) => ({
            list: ListOverviewUtils.enhanceListWithCalculatedUniqAmount(newList),
            isDeleted: false,
            isNew: !storedList
        });
    }

    changeListMetaDataViaSimpleSocket(newMetaData: MetaData) {
        return (storedList: ShoppingList | null | undefined) => ({
            list: storedList ? this.updateListMetaData(storedList, newMetaData) : null,
            isDeleted: false,
            isNew: false
        });
    }

    changeItemsViaSimpleSocket(itemsToAdd: ShoppingListItem[], itemsToRemove: ShoppingListItem[]) {
        return (storedList: ShoppingList | null | undefined) => {
            if (!storedList) {
                return {
                    list: null,
                    isDeleted: false,
                    isNew: false
                };
            }
            const updatedList = ListPageUtils.getUpdatedItemsList(storedList, itemsToAdd, itemsToRemove);
            return {
                list: ListOverviewUtils.enhanceListWithCalculatedUniqAmount(updatedList),
                isDeleted: false,
                isNew: false
            };
        };
    }

    removeListViaSimpleSocket() {
        return (storedList: ShoppingList | null | undefined) => ({
            list: storedList,
            isDeleted: true,
            isNew: false
        });
    }
}

export default new SimpleSocketListStore();
